import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useEffect, useImperativeHandle, useRef, useState, } from 'react';
import styled, { css } from 'styled-components';
import { getEnhancedVideoProperties, getPreferredOrientationAdjustmentAngle, useScreenOrientation, } from '../utils';
import { useStreamrTvViewer } from '../utils/useStreamrTvViewer';
export function Canvas({ fit: fitProp = 'auto', streamId, controllerRef, onReady, onReceivedFrame, onMuteChange, onVideoPropertiesChange: onVideoPropertiesChangeProp, followPreferredOrientation = false, overlay, overlayFollowVideoOrientation = false, ...props }) {
    const [canvas, setCanvas] = useState(null);
    const containerRef = useRef(null);
    const [videoProperties, setVideoProperties] = useState();
    const fit = fitProp === 'auto'
        ? videoProperties?.type === 'screen'
            ? 'contain'
            : 'cover'
        : fitProp;
    const screenOrientation = useScreenOrientation();
    useEffect(() => {
        function onResize() {
            const container = containerRef.current;
            const parent = canvas?.parentElement?.parentElement;
            if (!parent || !container || videoProperties == null) {
                return;
            }
            const { width: intrinsicWidth, height: intrinsicHeight, rotatedWidth, rotatedHeight, rotationAngle: angle, } = followPreferredOrientation
                ? getEnhancedVideoProperties({
                    ...videoProperties,
                    videoOrientationAngle: videoProperties.videoOrientationAngle +
                        getPreferredOrientationAdjustmentAngle(videoProperties.preferredOrientation, screenOrientation),
                })
                : videoProperties;
            const scale = (fit === 'cover' ? Math.max : Math.min)(parent.clientWidth / rotatedWidth, parent.clientHeight / rotatedHeight);
            const canvasWidth = intrinsicWidth * scale;
            const canvasHeight = intrinsicHeight * scale;
            container.style.setProperty('--Canvas_Width', `${canvasWidth}px`);
            container.style.setProperty('--Canvas_Height', `${canvasHeight}px`);
            container.style.setProperty('--Canvas_OffsetX', `${parent.clientWidth / 2}px`);
            container.style.setProperty('--Canvas_OffsetY', `${parent.clientHeight / 2}px`);
            container.style.setProperty('--Canvas_Rotation', `${angle}deg`);
            container.style.setProperty('--Rotated_Canvas_Width', `${rotatedWidth * scale}px`);
            container.style.setProperty('--Rotated_Canvas_Height', `${rotatedHeight * scale}px`);
        }
        onResize();
        window.addEventListener('resize', onResize);
        return () => {
            window.removeEventListener('resize', onResize);
        };
    }, [
        canvas,
        videoProperties,
        fit,
        screenOrientation,
        followPreferredOrientation,
    ]);
    const viewer = useStreamrTvViewer({
        streamId,
        canvas: canvas || undefined,
        onStarted: () => {
            onReady?.();
        },
        onReceivedFrame: () => {
            onReceivedFrame?.();
        },
    });
    const onMuteChangeRef = useRef(onMuteChange);
    if (onMuteChangeRef.current !== onMuteChange) {
        onMuteChangeRef.current = onMuteChange;
    }
    const onVideoPropertiesChangeRef = useRef(onVideoPropertiesChangeProp);
    if (onVideoPropertiesChangeRef.current !== onVideoPropertiesChangeProp) {
        onVideoPropertiesChangeRef.current = onVideoPropertiesChangeProp;
    }
    useEffect(() => {
        if (!viewer) {
            return;
        }
        let mounted = true;
        function onMuteChange() {
            if (mounted && viewer) {
                onMuteChangeRef.current?.(viewer.isAudioMuted());
            }
        }
        onMuteChange();
        viewer.on('muteChange', onMuteChange);
        function onVideoPropertiesChange(value) {
            const enhanced = getEnhancedVideoProperties(value);
            setVideoProperties(enhanced);
            onVideoPropertiesChangeRef.current?.(enhanced);
        }
        const videoProperties = viewer.getVideoProperties();
        if (videoProperties) {
            onVideoPropertiesChange(videoProperties);
        }
        viewer.on('videoPropertiesChanged', onVideoPropertiesChange);
        return () => {
            mounted = false;
            viewer.off('videoPropertiesChanged', onVideoPropertiesChange);
            viewer.off('muteChange', onMuteChange);
        };
    }, [viewer]);
    useImperativeHandle(controllerRef, () => ({
        mute: () => {
            viewer?.muteAudio();
        },
        unmute: () => {
            viewer?.unmuteAudio();
        },
    }), [viewer]);
    return (_jsxs(CanvasContainer, { ref: containerRef, children: [_jsx(CanvasElement, { ...props, ref: setCanvas }), overlay && (_jsx(CanvasOverlayWrapper, { children: _jsx(CanvasOverlayContent, { rotateWithVideo: overlayFollowVideoOrientation, children: overlay }) }))] }));
}
const CanvasContainer = styled.div `
    width: var(--Canvas_Width, 100%);
    height: var(--Canvas_Height, 100%);
    transform: translate(-50%, -50%)
        translate(var(--Canvas_OffsetX, 0), var(--Canvas_OffsetY, 0))
        rotate(var(--Canvas_Rotation, 0deg));
    transform-origin: center;
    position: relative;
`;
const CanvasElement = styled.canvas `
    width: 100%;
    height: 100%;
`;
const CanvasOverlayWrapper = styled.div `
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    pointer-events: none;
`;
const CanvasOverlayContent = styled.div `
    pointer-events: auto;
    position: absolute;
    transform-origin: center;

    ${({ rotateWithVideo }) => rotateWithVideo === true
    ? css `
                width: var(--Rotated_Canvas_Width);
                  height: var(--Rotated_Canvas_Height);
                  left: 50%;
                  top: 50%;
                  transform: translate(-50%, -50%) rotate(calc(-1 * var(--Canvas_Rotation, 0deg)));
              `
    : css `
                  width: 100%;
                  height: 100%;
                  top: 0;
                  left: 0;
              `}
`;
